import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useModal } from 'react-modal-hook'
import { Formik, Form, Field } from 'formik'
import NeoButton from '../../../components/NeoButton/NeoButton'
import NeoInput from '../../../components/NeoInput/NeoInput'
import Loading from 'custom/assets/loaders/Loading'
import DepositsService from '../../../services/deposits.service'
import Modal from '../../../components/Modal/Modal'
import { CreateTermDepositSchema } from '../../../schemas/deposit'
import InputError from '../../../components/InputError/InputError'
import { useTranslation } from 'react-i18next'
import {
  DIALOG_KEYS,
  DEPOSITS_KEYS,
  SIDENAV_KEYS,
  VALIDATION_MESSAGES,
} from '../../../utils/translation-constants'
import NeoOTP from '../../../components/FormComponents/NeoOTP'
import NeoSuccess from '../../../components/FormComponents/NeoSuccess'
import { useHistory } from 'react-router-dom'
import NumberFormat from 'react-number-format'
import LayoutWithOutAccountCard from '../../../layout/Layout/layout-without-card'
import AccountsService from '../../../services/accounts.service'
import TermsAndConditions from '../../../components/TermsAndConditions/TermsAndCondition'
import { getFeatureListSelector } from '../../../selectors'

import validationConfig from 'custom/config/validation.config.json'
import config from 'custom/config/environment'
import { object } from 'yup'
const depositConfig = validationConfig['deposit']

export default function CreateTermDeposits() {
  const { t } = useTranslation()
  const [stage, setStage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [autoRen, setAutoRen] = useState(false)
  const [otp, setOtp] = useState('')
  const [formValues, setFormValues] = useState({})
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const dispatch = useDispatch()
  const history = useHistory()
  const [verifyResponse, setVerifyResponse] = useState(false)
  const [tenorLists, setTenorLists] = useState([])
  const [currList, setCurrList] = useState([])
  const [periodList, setPeriodList] = useState([])
  const [otpEnabled, setOtpEnabled] = useState(false)
  const [accounts, setAccounts] = useState([])
  const [verifyDetails, setVerifyDetails] = useState({})
  const [interestRateList, setInterestRateList] = useState([])
  const featureList = useSelector(getFeatureListSelector)
  const [requestValues, setRequestValues] = useState({})
  const [productList, setProductList] = useState([])
  const [expand, setExpand] = useState(null)
  const [selectedProduct, setSelectedProduct] = useState({})
  const [disableDays, setDisableDays] = useState(false)
  const [disableMonths, setDisableMonths] = useState(false)
  const [disableYears, setDisableYears] = useState(false)

  const paymentOptions = {
    R: t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.CREDIT_ON_MATURITY),
    M: t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.CREDIT_INTEREST_MONTHLY),
    Q: t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.CREDIT_INTEREST_QUARTERLY),
  }

  const [showModal, hideModal] = useModal(
    () => (
      <Modal>
        <div className='modal-error-body'>
          <h6>
            <b>{error?.code}</b> : {error?.message}
          </h6>
          <div className='modal-btn-style'>
            <NeoButton
              mode='newDesign'
              type='button'
              text={t(DIALOG_KEYS.CLOSE)}
              onClick={() => {
                hideModal()
              }}
            />
          </div>
        </div>
      </Modal>
    ),
    [error],
  )

  const fetchCurrencies = useCallback(async () => {
    try {
      const response = await DepositsService.getDepositCurrencyList()
      if (response.status === 200) {
        setCurrList(response.data['currencyList'])
      }
    } catch (err) {
      setLoading(false)
    }
  }, [])

  const getProducts = useCallback(async () => {
    try {
      const response = await DepositsService.getProducts()
      if (response.status === 200) {
        response.data['termDepositProductList'].forEach((ele) => (ele.active = false))
        setProductList(response.data['termDepositProductList'])
      }
    } catch (err) {
      setLoading(false)
    }
  }, [])

  const fetchInterestList = useCallback(async () => {
    try {
      const response = await DepositsService.getTenorLists()
      if (response.status === 200) {
        setInterestRateList(response.data['tdInterestRatesList'])
      }
    } catch (err) {
      setLoading(false)
    }
  }, [])

  const fetchAccounts = useCallback(async () => {
    const response = await AccountsService.getAllAccounts()
    if (response.status === 200) {
      const casaAccountList = response.data['accountSummaryDetails']
      setAccounts(casaAccountList)
      setStage(1)
    }
  }, [])

  useEffect(() => {
    fetchAccounts()
    fetchCurrencies()
    // fetchInterestList()
    getProducts()
  }, [])

  const onConfirm = useCallback(async () => {
    try {
      setLoading(true)
      const response = await DepositsService.confirmTermDeposit({
        ...requestValues,
        otpNo: otp || '0000',
        product: verifyDetails['product'],
      })
      if (response.status === 200) {
        setMessage(response.data['message'])
        setLoading(false)
        setStage(4)
      }
    } catch (err) {
      setLoading(false)
    }
  }, [formValues, autoRen, otp, accounts, requestValues, verifyDetails])

  const onVerify = useCallback(
    async (data, { setSubmitting }) => {
      try {
        const creditAccountDetails = accounts.filter(
          (value) => value?.accountNo === data.creditAccNo,
        )[0]

        const debitAccountDetails = accounts.filter(
          (value) => value?.accountNo === data.debitAccNo,
        )[0]

        const featureDetails = featureList['TD_CREATE']
        if (featureDetails[debitAccountDetails['accountType']] === 'N') {
          setError({
            code: t(DIALOG_KEYS.NO_ACCESS),
            message: t(DIALOG_KEYS.NO_ACCOUNTS),
          })
          showModal()
          return
        }

        if (Object.keys(selectedProduct).length === 0) {
          setError({
            code: t(DIALOG_KEYS.NO_ACCESS),
            message: t(DIALOG_KEYS.CHOOSE_PRODUCT),
          })
          showModal()
          return
        }

        // if (data.debitAccNo === data.creditAccNo) {
        //   setError({
        //     code: 'Error',
        //     message: t(DIALOG_KEYS.DEBIT_CREDIT_NOT_SAME),
        //   })
        //   showModal()
        //   return
        // }

        if (data.amount === '0.00' || data.amount === '.00') {
          return
        }
        setFormValues(data)
        setLoading(true)
        const reqParams = {
          autoRenewal: autoRen ? 'AUTO_RENEWAL' : 'CLOSE_ON_MATURITY',
          principle: data.principleType === 'principle',
          branchCode: debitAccountDetails.brnCode,
          currency: data.toCurrency,
          product: selectedProduct?.product,
          payInAmt: +data.amount,
          debitAccNo: debitAccountDetails.accountNo,
          debitAccBrn: debitAccountDetails.brnCode,
          months: data.tenureMonths || '0',
          years: data.tenureYears || '0',
          days: data.tenureDays || '0',
          interestCredit: data.interestCredit,
          creditAccNo: creditAccountDetails.accountNo,
          creditAccBrn: creditAccountDetails.brnCode,
          cdf: {
            customerName: debitAccountDetails.customerName,
            customerNo: debitAccountDetails.custNo,
          },
        }
        const response = await DepositsService.createTermDeposit(reqParams)
        if (response.status === 200) {
          setLoading(false)
          setOtpEnabled(response.data['otpEnable'])
          setVerifyDetails(response.data)
          setRequestValues(reqParams)
          setStage(2)
        }
      } catch (err) {
        setLoading(false)
      }
    },
    [accounts, autoRen, selectedProduct],
  )

  const handleProduct = useCallback(
    (option) => {
      const productCopy = [...productList]
      productCopy.forEach((ele) => {
        if (ele.product === option.product) {
          ele.active = true
        } else {
          ele.active = false
        }
      })
      setProductList(productCopy)
      setSelectedProduct(option)
    },
    [productList],
  )

  function renderSwitch() {
    switch (stage) {
      case 0:
        return renderLoading()
      case 1:
        return renderForm()
      case 2:
        return renderConfirmation()
      case 3:
        return <NeoOTP otp={otp} setOTP={setOtp} onSubmit={onConfirm} loading={loading} />
      case 4:
        return <NeoSuccess message={message} linkPath='/deposits' />
      default:
        return renderForm()
    }
  }

  function renderForm() {
    return (
      <div>
        <div className='heading-style'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.SUBTITLE)}</div>
        <Formik
          initialValues={{
            debitAccNo: '',
            creditAccNo: '',
            toCurrency: '',
            interestCredit: '',
            amount: '',
            tenorPeriod: '',
            tenorValue: '',
            tenureMonths: '',
            tenureDays: '',
            tenureYears: '',
            depositType: config.TD_CLOSE_INSTRUCTION ? 'closeOnMaturity' : '',
            principleType: config.TD_CLOSE_INSTRUCTION ? 'principle' : '',
          }}
          enableReinitialize
          validationSchema={CreateTermDepositSchema}
          onSubmit={onVerify}>
          {({ isSubmitting, errors, touched, handleChange, setFieldValue, values }) => (
            <Form>
              <div className='form-group my-4'>
                {config.TD_CLOSE_INSTRUCTION && (
                  <div className='d-flex justify-content-center align-items-center'>
                    <div className='custom-control custom-radio custom-control-inline'>
                      <Field
                        as='input'
                        type='radio'
                        id='closeOnMaturity'
                        name='depositType'
                        value='closeOnMaturity'
                        className='custom-control-input'
                        onClick={() => setAutoRen(false)}
                      />
                      <label className='custom-control-label' htmlFor='closeOnMaturity'>
                        {t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.CLOSE_ON_MATURITY)}
                      </label>
                    </div>
                    <div className='custom-control custom-radio custom-control-inline'>
                      <Field
                        as='input'
                        value='autoRenewal'
                        type='radio'
                        id='autoRenewal'
                        name='depositType'
                        className='custom-control-input'
                        onClick={() => setAutoRen(true)}
                      />
                      <label className='custom-control-label' htmlFor='autoRenewal'>
                        {t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.AUTO_RENEWAL)}
                      </label>
                    </div>
                  </div>
                )}
                {autoRen ? (
                  <div className='mt-3 d-flex justify-content-center align-items-center'>
                    <div className='custom-control custom-radio custom-control-inline new-style-radio'>
                      <Field
                        as='input'
                        type='radio'
                        id='principle'
                        name='principleType'
                        value='principle'
                        className='custom-control-input'
                      />
                      <label className='custom-control-label' htmlFor='principle'>
                        {t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.ROLLOVER_PRINCIPAL)}
                      </label>
                    </div>
                    <div className='custom-control custom-radio custom-control-inline new-style-radio'>
                      <Field
                        as='input'
                        value='principleInterest'
                        type='radio'
                        id='principleInterest'
                        name='principleType'
                        className='custom-control-input'
                      />
                      <label className='custom-control-label' htmlFor='principleInterest'>
                        {t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.ROLLOVER_PRINCIPAL_INTEREST)}
                      </label>
                    </div>
                  </div>
                ) : (
                  <div />
                )}
                <div className='d-flex row'>
                  <div className='form-group col-12'>
                    <NeoInput
                      tabIndex='1'
                      showAsterisk={depositConfig['isAccountNoRequired']}
                      text={t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.CREDIT_ACCOUNT_NO)}
                      name='creditAccNo'
                      mode='select'
                      list={accounts}
                      keyValue='accountNo'
                      onChange={handleChange}
                    />
                    <InputError text={errors.creditAccNo} touched={touched.creditAccNo} />
                  </div>

                  <div className='form-group col-12'>
                    <NeoInput
                      tabIndex='2'
                      showAsterisk={depositConfig['isAccountNoRequired']}
                      text={t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.DEBIT_ACCOUNT_NO)}
                      name='debitAccNo'
                      mode='select'
                      list={accounts}
                      keyValue='accountNo'
                      onChange={handleChange}
                    />
                    <InputError text={errors.debitAccNo} touched={touched.debitAccNo} />
                  </div>

                  <div className='form-group  col-12'>
                    <NeoInput
                      tabIndex='3'
                      showAsterisk={depositConfig['isCurrencyRequired']}
                      text={t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.CURRENCY)}
                      name='toCurrency'
                      keyValue={null}
                      list={currList}
                      mode='select'
                      onChange={handleChange}
                    />
                    <InputError text={errors.toCurrency} touched={touched.toCurrency} />
                  </div>

                  <div className='form-group col-12'>
                    <label className='control-label'>
                      {t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.PAYMENT_METHOD)}{' '}
                    </label>
                    <Field
                      tabIndex={'4'}
                      as='select'
                      name='interestCredit'
                      className='form-control'
                      onChange={handleChange}>
                      <option value='' />
                      {config.TD_PAYMENT_METHOD.map((option, index) => (
                        <option value={option} key={index}>
                          {paymentOptions[option]}
                        </option>
                      ))}
                    </Field>
                    <InputError text={errors.interestCredit} touched={touched.interestCredit} />
                  </div>
                  <div className='form-group col-12'>
                    <label className='control-label'>
                      {t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.CHOOSE_PRODUCT)}
                    </label>
                    <div className='row product-code-wrapper'>
                      {productList.map((option, index) => (
                        <div className='col-6'>
                          <div
                            key={index}
                            className={
                              option.active ? 'product-card product-active' : 'product-card'
                            }
                            onClick={() => {
                              if (config.TD_TENURE_DEFAULT_FROM_PRODUCT) {
                                setFieldValue('tenureYears', option.tenorYears)
                                setFieldValue('tenureMonths', option.tenorMonths)
                                setFieldValue('tenureDays', option.tenorDays)
                              }

                              handleProduct(option)
                            }}>
                            <div
                              className={expand === index ? 'product-expand' : 'product-collapse'}>
                              <h6>{option.name}</h6>
                              <p style={{ fontWeight: 600 }}>
                                {t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.FEATURES)} :
                              </p>
                              <p>{option.description}</p>
                              <ul>
                                <li>
                                  {t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.YEARS)} : {option?.tenorYears}
                                </li>
                                <li>
                                  {t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.MONTHS)} :{' '}
                                  {option?.tenorMonths}
                                </li>
                                <li>
                                  {t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.DAYS)} : {option?.tenorDays}
                                </li>
                              </ul>
                            </div>
                            <p
                              className='show-more'
                              onClick={() => {
                                if (expand === index) {
                                  setExpand(null)
                                } else {
                                  setExpand(index)
                                }
                              }}>
                              {expand === index && expand !== null
                                ? t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.SHOW_LESS)
                                : t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.SHOW_MORE)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className='form-group col-12'>
                    <label className='control-label'>
                      {t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.TENURE)}{' '}
                      {depositConfig['isTenureRequired'] ? '*' : ''}
                    </label>
                    <div className='row tenure-values'>
                      <div className='col-4'>
                        <NeoInput
                          tabIndex='6'
                          mode='onlyNumber'
                          onChange={(values) => {
                            setFieldValue('tenureYears', values.value)
                            if (config.TD_TENURE_ONLY_ONE && values?.value?.length) {
                              setDisableDays(true)
                              setDisableMonths(true)
                            } else {
                              setDisableDays(false)
                              setDisableMonths(false)
                            }
                          }}
                          value={values.tenureYears}
                          name='tenureYears'
                          disabled={disableYears}
                          text={t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.YEARS)}
                        />
                      </div>
                      <div className='col-4'>
                        <NeoInput
                          tabIndex='7'
                          mode='onlyNumber'
                          onChange={(values) => {
                            setFieldValue('tenureMonths', values.value)
                            if (config.TD_TENURE_ONLY_ONE && values?.value?.length) {
                              setDisableDays(true)
                              setDisableYears(true)
                            } else {
                              setDisableDays(false)
                              setDisableYears(false)
                            }
                          }}
                          value={values.tenureMonths}
                          name='tenureMonths'
                          disabled={disableMonths}
                          text={t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.MONTHS)}
                        />
                      </div>
                      <div className='col-4'>
                        <NeoInput
                          tabIndex='8'
                          mode='onlyNumber'
                          onChange={(values) => {
                            setFieldValue('tenureDays', values.value)
                            if (config.TD_TENURE_ONLY_ONE && values?.value?.length) {
                              setDisableMonths(true)
                              setDisableYears(true)
                            } else {
                              setDisableMonths(false)
                              setDisableYears(false)
                            }
                          }}
                          value={values.tenureDays}
                          name='tenureDays'
                          disabled={disableDays}
                          text={t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.DAYS)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* <div className='form-group col-12'>
                    <NeoInput
                      showAsterisk={depositConfig['isTenureValueRequired']}
                      mode='onlyNumber'
                      text={t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.TENURE_VALUE)}
                      onChange={(values) => {
                        setFieldValue('tenorValue', values.value)
                      }}
                      name='tenorValue'
                    />
                    <InputError text={errors.tenorValue} touched={touched.tenorValue} />
                  </div> */}
                </div>
                <div className='d-flex row'>
                  <div className='form-group col-12'>
                    <NeoInput
                      tabIndex='9'
                      showAsterisk={depositConfig['isAmountRequired']}
                      mode='amount'
                      text={t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.AMOUNT)}
                      onChange={(values) => {
                        setFieldValue('amount', values.value)
                      }}
                      name='depositAmount'
                    />
                    <InputError text={errors.amount} touched={touched.amount} />
                    <InputError
                      text={t(VALIDATION_MESSAGES.AMOUNT_VALID)}
                      touched={values.amount === '0.00' || values.amount === '.00'}
                    />
                  </div>
                </div>
                <div className='d-flex row'></div>
                <div className='mt-3'>
                  {loading ? (
                    renderLoading()
                  ) : (
                    <NeoButton
                      tabIndex='10'
                      type='submit'
                      mode='newDesign'
                      disabled={loading}
                      text={t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.CREATE_BUTTON)}
                    />
                  )}
                </div>
                <div className='back-cancel-btn-container'>
                <button className='back-cancel-btn btn btn-sm new-design-warning-text-color' tabIndex={'11'} onClick={() => history.goBack()}>
                  {' '}
                  {t(SIDENAV_KEYS.TOUCH_ID_KEYS.CANCEL)}
                </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  function renderConfirmation() {
    return (
      <div className='d-flex flex-column confirmation'>
        <h3>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.SUBTITLE_1)}</h3>
        {config.TD_CLOSE_INSTRUCTION && (
          <>
            {!autoRen ? (
              <div>
                <h6 className='label'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.DEPOSIT_TYPE)}</h6>
                <h6 className='value'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.CLOSE_ON_MATURITY)}</h6>
              </div>
            ) : (
              <div>
                <div>
                  <h6 className='label'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.DEPOSIT_TYPE)}</h6>
                  <h6 className='value'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.AUTO_RENEWAL)}</h6>
                </div>
                <div>
                  <h6 className='label'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.PRINCIPAL)}</h6>
                  <h6 className='value'>
                    {formValues.principleType === 'principle'
                      ? t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.ROLLOVER_PRINCIPAL)
                      : t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.ROLLOVER_PRINCIPAL_INTEREST)}
                  </h6>
                </div>
              </div>
            )}
          </>
        )}
        <div>
          <h6 className='label'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.CREDIT_ACCOUNT_NO)}</h6>
          <h6 className='value'>{formValues.creditAccNo}</h6>
        </div>

        <div>
          <h6 className='label'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.DEBIT_ACCOUNT_NO)}</h6>
          <h6 className='value'>{formValues.debitAccNo}</h6>
        </div>

        <div>
          <h6 className='label'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.CURRENCY)}</h6>
          <h6 className='value'>{formValues.toCurrency}</h6>
        </div>
        <div>
          <h6 className='label'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.PAYMENT)}</h6>
          <h6 className='value'>{paymentOptions[formValues.interestCredit]}</h6>
        </div>
        <div>
          <h6 className='label'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.AMOUNT_DETAILS)}</h6>
          <h6 className='value'>
            <NumberFormat
              value={formValues.amount}
              displayType={'text'}
              thousandSeparator={true}
              prefix={''}
              fixedDecimalScale={true}
            decimalScale={2}
            allowEmptyFormatting
            />
          </h6>
        </div>
        <div>
          <h6 className='label'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.TENURE_PERIOD)}</h6>
          <h6 className='value'>
            {formValues.tenureYears || '0'} Year {formValues.tenureMonths || '0'} Months{' '}
            {formValues.tenureDays || '0'} Days
          </h6>
        </div>

        <div>
          <h6 className='label'>{t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.INTEREST_RATE)}</h6>
          <h6 className='value'>{verifyDetails?.interestRate || '0'}</h6>
        </div>

        <div>
          <TermsAndConditions module="TERM_DEPOSITE" />
        </div>

        <div className='mt-3'>
          {loading ? (
            renderLoading()
          ) : (
            <NeoButton
              mode={'newDesign'}
              type='button'
              text={t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.CONFIRM_BUTTON)}
              onClick={() => (otpEnabled ? setStage(3) : onConfirm())}
            />
          )}
        </div>
        <div className='back-cancel-style' onClick={() => setStage(1)}>
          {' '}
          {t(SIDENAV_KEYS.TOUCH_ID_KEYS.BACK)}
        </div>
      </div>
    )
  }

  return (
    <LayoutWithOutAccountCard
      breadCumKey={'TERM_DEPOSIT'}
      showEmptySpace={true}
      keyPath={'DEPOSIT'}>
      <div className='new-design-form-style'>{renderSwitch()}</div>
    </LayoutWithOutAccountCard>
  )
}
